/**
 * Inline-Formular zum Anlegen eines neuen, freistehenden Prompts direkt im
 * Bulk-Wizard (Spec 2026-08-15 §4.1) — kein Modal, kein Seitenwechsel. Name
 * zeigt nur einen Platzhalter, kein vorausgefuellter Wert (verhindert
 * Namenskollisionen bei identischem naheliegendem Vorschlag).
 */

import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Input, Textarea } from '@/components/v2/ui'

import { DuplicateTemplateNameError, useCreateInlineTemplate } from './useCreateInlineTemplate'

interface InlineTemplateFormProps {
  channelLabel: string
  fieldLabel: string
  onCancel: () => void
  onCreated: (templateId: string) => void
}

export function InlineTemplateForm({ channelLabel, fieldLabel, onCancel, onCreated }: InlineTemplateFormProps) {
  const { t } = useTranslation()
  const nameId = useId()
  const styleId = useId()
  const [name, setName] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [duplicateError, setDuplicateError] = useState<string | null>(null)
  const [genericError, setGenericError] = useState<string | null>(null)
  const createTemplate = useCreateInlineTemplate()

  const handleSave = async () => {
    setDuplicateError(null)
    setGenericError(null)
    try {
      const template = await createTemplate.mutateAsync({ channelLabel, fieldLabel, name, systemPrompt })
      onCreated(template.id)
    } catch (err) {
      if (err instanceof DuplicateTemplateNameError) {
        setDuplicateError(err.message)
      } else {
        // apiClient rejects with a plain ApiError object (not an Error instance), so
        // duck-type on .message instead of `instanceof Error` to also cover it.
        const message = (err as { message?: string } | undefined)?.message
        setGenericError(message || 'Erstellung fehlgeschlagen. Bitte erneut versuchen.')
      }
    }
  }

  return (
    <div className="col-start-1 col-span-3 space-y-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {t('channelContent.wizard.newPromptFormTitle', { channel: channelLabel, field: fieldLabel })}
      </p>
      <Input
        id={nameId}
        label={t('channelContent.wizard.newPromptNameLabel')}
        placeholder={t('channelContent.wizard.newPromptNamePlaceholder')}
        value={name}
        onChange={(e) => {
          setName(e.target.value)
          setDuplicateError(null)
        }}
        error={duplicateError ?? undefined}
        disabled={createTemplate.isPending}
      />
      <Textarea
        id={styleId}
        label={t('channelContent.wizard.newPromptStyleLabel')}
        placeholder={t('channelContent.wizard.newPromptStylePlaceholder')}
        value={systemPrompt}
        onChange={(e) => setSystemPrompt(e.target.value)}
        disabled={createTemplate.isPending}
        rows={3}
      />
      {genericError && <p className="text-sm text-red-600 dark:text-red-400">{genericError}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={createTemplate.isPending}>
          {t('channelContent.wizard.newPromptCancel')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={createTemplate.isPending || !name.trim() || !systemPrompt.trim()}
        >
          {createTemplate.isPending
            ? t('channelContent.wizard.newPromptSaving')
            : t('channelContent.wizard.newPromptSave')}
        </Button>
      </div>
    </div>
  )
}
